// Lazy loader for muted ambient/decorative autoplay videos.
//
// Markup contract: wrap a <video> in an element carrying data-lazy-video.
// Videos ship with preload="none" and WITHOUT the autoplay attribute, so
// nothing downloads at page load (Safari otherwise fetches and plays even
// display:none autoplay videos).
//
// Modes (attribute value):
//   "visible" (default) — warm one viewport ahead, play while on screen,
//     pause off screen. A display:none copy (hidden desktop/mobile variant,
//     closed mega menu) never intersects, so it never downloads; when it
//     becomes visible (menu opens, breakpoint changes) the observer fires
//     and playback starts then.
//   "hover" — load + play only on pointerenter/focusin of the wrapper,
//     pause on pointerleave/focusout. For hover-reveal cards whose video is
//     invisible until :hover (never plays on touch devices, by design).
//
// play() is gated on canplay: calling play() on a cold video makes Safari
// drop the poster before the first frame is decoded, flashing blank.
;(function () {
  function warm(video) {
    if (video.dataset.lazyVideoWarmed) return
    video.dataset.lazyVideoWarmed = '1'
    video.preload = 'auto'
    if (video.readyState === 0) {
      try {
        video.load()
      } catch (e) {}
    }
  }

  function playWhenReady(video) {
    // A non-looping video that already finished should stay finished
    // (matching the old autoplay-once behaviour) instead of restarting
    // every time it re-enters the viewport.
    if (video.ended && !video.loop) return
    video.muted = true
    if (video.readyState >= 3) {
      video.play().catch(function () {})
      return
    }
    warm(video)
    if (video.dataset.lazyVideoPending) return
    video.dataset.lazyVideoPending = '1'
    var onReady = function () {
      video.removeEventListener('canplay', onReady)
      delete video.dataset.lazyVideoPending
      // Only start if the video is still supposed to be playing.
      if (video.dataset.lazyVideoWantsPlay === '1')
        video.play().catch(function () {})
    }
    video.addEventListener('canplay', onReady)
  }

  var warmObserver = null
  var playObserver = null

  function ensureObservers() {
    if (warmObserver) return
    // Warm one viewport ahead so playback can start the moment the video
    // scrolls into view.
    warmObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return
          warm(entry.target)
          warmObserver.unobserve(entry.target)
        })
      },
      { rootMargin: '100% 0px', threshold: 0 }
    )
    playObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target
          if (entry.isIntersecting) {
            video.dataset.lazyVideoWantsPlay = '1'
            playWhenReady(video)
          } else {
            video.dataset.lazyVideoWantsPlay = '0'
            if (!video.paused) video.pause()
          }
        })
      },
      { threshold: 0.1 }
    )
  }

  function bindHover(wrapper, video) {
    var start = function () {
      video.dataset.lazyVideoWantsPlay = '1'
      playWhenReady(video)
    }
    var stop = function () {
      video.dataset.lazyVideoWantsPlay = '0'
      if (!video.paused) video.pause()
    }
    wrapper.addEventListener('pointerenter', start)
    wrapper.addEventListener('focusin', start)
    wrapper.addEventListener('pointerleave', stop)
    wrapper.addEventListener('focusout', stop)
  }

  function scan(root) {
    var wrappers = (root || document).querySelectorAll('[data-lazy-video]')
    wrappers.forEach(function (wrapper) {
      var videos = wrapper.querySelectorAll('video')
      videos.forEach(function (video) {
        if (video.dataset.lazyVideoBound) return
        video.dataset.lazyVideoBound = '1'
        if (wrapper.getAttribute('data-lazy-video') === 'hover') {
          bindHover(wrapper, video)
        } else {
          ensureObservers()
          warmObserver.observe(video)
          playObserver.observe(video)
        }
      })
    })
  }

  window.initLazyAutoplayVideos = scan

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan()
    })
  } else {
    scan()
  }

  // Theme editor: sections re-render with fresh DOM nodes.
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target || document)
  })
})()
